// =============================================================================
// Normascan — Plateforme d'analyse normative par IA
// Copyright (c) 2026-2027 Evalens SAS — Tous droits réservés
// Marque Normascan déposée INPI n°5242986 (classes 9 et 42)
// Site : https://app.normascan.fr
// =============================================================================
import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { verifyMfaLogin, setupMfa, confirmMfaSetup } from '../services/api';
import MfaSetupFlow from '../components/MfaSetupFlow';
import AcceptationCgv from '../components/AcceptationCgv';
import { useI18n } from '../i18n';
import { useSignupEnabled } from '../lib/useSignupEnabled';
import { doitChoisirUnForfait } from '../lib/acces';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://app.normascan.fr';

function Login() {
  const { t } = useI18n();
  const signupEnabled = useSignupEnabled();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  // ✅ R-06 : étape CGU si premier login — ou si la version acceptée n'est plus
  // celle en vigueur (le serveur rend alors `cgu_accepted: false`, cf `core/cgv`).
  const [cguStep, setCguStep] = useState(false);
  // Compte gardé ici TANT QUE les CGU ne sont pas acceptées : il n'est publié
  // dans le contexte (`login`) qu'après. La session, elle, est déjà ouverte —
  // le serveur a posé ses cookies (chantier ⑯) ; refuser les CGU la ferme.
  const [pendingUser, setPendingUser] = useState(null);
  // MFA : distinct de cguStep — la session n'existe pas encore
  // tant que le 2e facteur n'est pas vérifié (temp_token restreint uniquement)
  const [mfaStep, setMfaStep] = useState(null); // null | 'verify' | 'setup_required'
  const [mfaTempToken, setMfaTempToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');
  const [mfaLoading, setMfaLoading] = useState(false);
  const [mfaError, setMfaError] = useState('');
  const { login, sessionExpired, setSessionExpired } = useAuth();
  // ✅ V3.2.1: Détecter ?expired=true dans l'URL (Scénario B — redirect depuis api.js)
  const [showExpiredBanner, setShowExpiredBanner] = useState(false);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('expired') === 'true') {
      setShowExpiredBanner(true);
      // Nettoyer l'URL sans recharger (retire ?expired=true)
      window.history.replaceState({}, '', '/login');
    }
  }, []);

  // ✅ Combiner les deux sources : contexte React (scénario A) OU query param (scénario B)
  const isExpired = sessionExpired || showExpiredBanner;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Erreur de connexion');
      }

      // ✅ Clear les bandeaux expiration
      if (sessionExpired) setSessionExpired(false);
      setShowExpiredBanner(false);

      // MFA : code à saisir (compte déjà configuré) — pas de token final encore
      if (data.status === 'mfa_required') {
        setMfaTempToken(data.temp_token);
        setMfaStep('verify');
        setLoading(false);
        return;
      }

      // MFA : configuration obligatoire (admin/dev sans MFA) — setup forcé avant tout accès
      if (data.status === 'mfa_setup_required') {
        setMfaTempToken(data.temp_token);
        setMfaStep('setup_required');
        setLoading(false);
        return;
      }

      // ✅ R-06 : si CGU non acceptées, garder le compte en attente (PAS de login())
      if (data.user && data.user.cgu_accepted === false) {
        setPendingUser(data.user);
        setLoading(false);
        setCguStep(true);
        return;
      }

      // CGU déjà acceptées → login normal
      login(data.user);

      // ✅ ASTUCE : Créer un formulaire visible avec les bonnes valeurs
      // Chrome va le détecter AVANT la redirection
      const form = e.target; // Réutiliser le formulaire existant

      // S'assurer que les inputs ont les bonnes valeurs
      const emailInput = form.querySelector('input[name="email"]');
      const passwordInput = form.querySelector('input[name="password"]');

      if (emailInput) emailInput.value = email;
      if (passwordInput) passwordInput.value = password;

      // ✅ Attendre 500ms pour que Chrome détecte le formulaire rempli
      const needsBilling = doitChoisirUnForfait(data.user);
      setTimeout(() => {
        window.location.href = needsBilling ? '/parametres?tab=billing' : '/dashboard';
      }, 500);

    } catch (err) {
      console.error('❌ Erreur login:', err);
      setError(err.message || 'Erreur de connexion');
      setLoading(false);
    }
  };

  // Partagé par le verify (code déjà configuré) et le setup forcé (1er code
  // valide) — reproduit la même logique CGU que handleSubmit après un login réussi.
  const completeMfaLogin = (data) => {
    if (data.user && data.user.cgu_accepted === false) {
      setPendingUser(data.user);
      setMfaStep(null);
      setCguStep(true);
      return;
    }
    login(data.user);
    const needsBilling = doitChoisirUnForfait(data.user);
    window.location.href = needsBilling ? '/parametres?tab=billing' : '/dashboard';
  };

  const handleMfaVerify = async (e) => {
    e.preventDefault();
    setMfaError('');
    if (!mfaCode.trim()) { setMfaError(t('mfa_error_invalid_code')); return; }
    setMfaLoading(true);
    try {
      const data = await verifyMfaLogin(mfaTempToken, mfaCode.trim());
      completeMfaLogin(data);
    } catch (err) {
      setMfaError(err.message || t('mfa_error_invalid_code'));
    } finally {
      setMfaLoading(false);
    }
  };

  const handleMfaBackToLogin = () => {
    setMfaStep(null);
    setMfaTempToken(null);
    setMfaCode('');
    setMfaError('');
  };

  // Appelé par `AcceptationCgv` une fois l'acceptation enregistrée par le
  // serveur : maintenant seulement, le compte est publié dans le contexte.
  const terminerAcceptationCgu = () => {
    const updatedUser = { ...pendingUser, cgu_accepted: true };
    login(updatedUser);
    const needsBilling = doitChoisirUnForfait(updatedUser);
    window.location.href = needsBilling ? '/parametres?tab=billing' : '/dashboard';
  };

  if (mfaStep === 'setup_required') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-md w-full space-y-4 p-8 bg-white rounded-lg shadow-lg text-center">
          <h2 className="text-2xl font-bold text-gray-900">
            {t('login_mfa_setup_required_title')}
          </h2>
          <p className="text-sm text-gray-700 leading-relaxed">
            {t('login_mfa_setup_required_intro')}
          </p>
        </div>
        <MfaSetupFlow
          onSetup={() => setupMfa(mfaTempToken)}
          onConfirm={(code) => confirmMfaSetup(code, mfaTempToken)}
          onClose={handleMfaBackToLogin}
          onComplete={(result) => completeMfaLogin(result)}
        />
      </div>
    );
  }

  if (mfaStep === 'verify') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-md w-full space-y-6 p-8 bg-white rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-center text-gray-900">
            {t('login_mfa_verify_title')}
          </h2>
          <p className="text-sm text-gray-600 text-center">
            {t('login_mfa_verify_intro')}
          </p>

          {mfaError && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded text-sm">
              {mfaError}
            </div>
          )}

          <form onSubmit={handleMfaVerify} className="space-y-4">
            <input
              type="text"
              value={mfaCode}
              onChange={(e) => setMfaCode(e.target.value)}
              placeholder={t('mfa_code_placeholder')}
              autoFocus
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm text-center text-lg tracking-widest focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="text-xs text-gray-500 text-center">{t('login_mfa_recovery_hint')}</p>
            <button
              type="submit"
              disabled={mfaLoading}
              className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
            >
              {mfaLoading ? t('mfa_verifying') : t('mfa_confirm_cta')}
            </button>
            <button
              type="button"
              onClick={handleMfaBackToLogin}
              className="w-full text-center text-sm text-gray-500 hover:text-gray-700"
            >
              {t('login_mfa_back_to_login')}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Même fenêtre qu'en cours de session (`AppLayout`) : un seul texte, une seule
  // logique de refus. Ses chaînes passent par le catalogue — l'ancien écran les
  // portait en dur.
  if (cguStep) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50">
        <AcceptationCgv utilisateur={pendingUser} onAcceptee={terminerAcceptationCgu} />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="max-w-md w-full space-y-8 p-8 bg-white rounded-lg shadow-lg">
        <div>
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <span className="text-white text-3xl font-bold">N</span>
            </div>
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Connexion
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Accédez à votre espace Normascan
          </p>
        </div>
         {isExpired && (
          <div style={{
            padding: '12px 16px',
            marginBottom: '16px',
            borderRadius: '8px',
            backgroundColor: '#fef3c7',
            borderLeft: '4px solid #f59e0b',
            color: '#92400e',
            fontSize: '14px'
          }}>
            ⏱️ Votre session a expiré. Veuillez vous reconnecter.
          </div>
        )}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="votre@email.com"
              />
            </div>
            
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Mot de passe
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder="••••••••"
              />
              <div className="mt-2 text-right">
                <a href="/forgot-password" className="text-sm text-blue-600 hover:underline">
                  {t('pwd_forgot_link')}
                </a>
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            {loading ? 'Connexion...' : 'Se connecter'}
          </button>
        </form>
        {/* Inscription self-service : le lien n'apparaît que si le serveur
            l'autorise (GET /api/info). `null` = réponse pas encore arrivée, on
            n'affiche rien plutôt que de faire clignoter un lien. */}
        <div className="mt-4 text-center text-sm">
          {signupEnabled === true ? (
            <p className="text-gray-600">
              {t('login_no_account_prompt')}{' '}
              <a href="/signup" className="text-blue-600 hover:underline font-medium">
                {t('login_signup_link')}
              </a>
            </p>
          ) : signupEnabled === false ? (
            <p className="text-gray-600">{t('signup_closed_contact')}</p>
          ) : null}
        </div>

        {/* Aide — SEUL point d'entrée PUBLIC vers /aide (31/08/2026). Le menu
            utilisateur qui y conduit demande d'être connecté, et `Footer.js`
            n'est monté nulle part. Or les deux motifs de consultation les plus
            fréquents — « je n'arrive plus à me connecter », « double
            authentification » — se vivent précisément sur CET écran.
            Français en dur, comme le paragraphe CGU/Confidentialité plus haut :
            /aide est un document, pas de l'UI — aucune clé i18n requise. */}
        <div className="mt-3 text-center text-sm">
          <a href="/aide" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:underline">
            Besoin d’aide ?
          </a>
        </div>
      </div>
    </div>
  );
}

export default Login;